import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  Link as LinkIcon,
  Undo2,
  Redo2,
} from 'lucide-react';
import { hydratePlainTextEmailBody } from '../../utils/emailTemplateHtml';
import './EmailTemplateRichEditor.css';

const SWATCHES = ['#e2e8f0', '#f87171', '#fbbf24', '#4ade80', '#38bdf8', '#a78bfa', '#f472b6'];

export type EmailTemplateRichEditorProps = {
  content: string;
  onChange: (html: string) => void;
  editable?: boolean;
  placeholder?: string;
  className?: string;
};

export const EmailTemplateRichEditor: React.FC<EmailTemplateRichEditorProps> = ({
  content,
  onChange,
  editable = true,
  placeholder = 'Write your message…',
  className = '',
}) => {
  const hydrated = hydratePlainTextEmailBody(content);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
          class: 'text-sky-400 underline',
        },
      }),
      TextStyle,
      Color,
    ],
    content: hydrated,
    editable,
    editorProps: {
      attributes: {
        class: 'email-template-prose focus:outline-none min-h-[140px] px-3 py-2 text-sm text-slate-200',
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editable, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
  }, [editor]);

  if (!editor) return <div className="rounded-lg border border-white/10 bg-slate-900/50 min-h-[140px]" />;

  return (
    <div className={`rounded-lg border border-white/10 bg-slate-900/50 overflow-hidden email-template-editor-root ${className}`}>
      {editable && (
        <div className="flex flex-wrap items-center gap-1 border-b border-white/10 bg-slate-800/80 px-2 py-1.5">
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Bold"
          >
            <BoldIcon className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Italic"
          >
            <ItalicIcon className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Bullet list"
          >
            <ListIcon className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="Numbered list"
          >
            <ListOrderedIcon className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn onClick={setLink} active={editor.isActive('link')} title="Link">
            <LinkIcon className="h-4 w-4" />
          </ToolbarBtn>
          <span className="mx-1 h-5 w-px bg-white/20" aria-hidden />
          {SWATCHES.map((c) => (
            <button
              key={c}
              type="button"
              title={`Color ${c}`}
              className="h-5 w-5 rounded border border-white/20 shrink-0"
              style={{ backgroundColor: c }}
              onClick={() => editor.chain().focus().setColor(c).run()}
            />
          ))}
          <button
            type="button"
            title="Remove color"
            className="text-[10px] px-1.5 py-0.5 rounded text-slate-400 hover:text-white hover:bg-white/10"
            onClick={() => editor.chain().focus().unsetColor().run()}
          >
            Clear
          </button>
          <span className="mx-1 h-5 w-px bg-white/20" aria-hidden />
          <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
            <Undo2 className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
            <Redo2 className="h-4 w-4" />
          </ToolbarBtn>
        </div>
      )}
      {editable && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
          <div className="flex gap-1 rounded-md border border-slate-600 bg-slate-800 p-1 shadow-lg">
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleBold().run()}
              active={editor.isActive('bold')}
              title="Bold"
            >
              <BoldIcon className="h-4 w-4" />
            </ToolbarBtn>
            <ToolbarBtn
              onClick={() => editor.chain().focus().toggleItalic().run()}
              active={editor.isActive('italic')}
              title="Italic"
            >
              <ItalicIcon className="h-4 w-4" />
            </ToolbarBtn>
            <ToolbarBtn onClick={setLink} active={editor.isActive('link')} title="Link">
              <LinkIcon className="h-4 w-4" />
            </ToolbarBtn>
          </div>
        </BubbleMenu>
      )}
      <EditorContent editor={editor} />
    </div>
  );
};

const ToolbarBtn: React.FC<{
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active, title, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`p-1.5 rounded-md transition-colors ${
      active ? 'bg-cyan-500/30 text-cyan-200' : 'text-slate-300 hover:bg-white/10 hover:text-white'
    }`}
  >
    {children}
  </button>
);
